import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  FiMail,
  FiPhone,
  FiMapPin,
  FiInstagram,
  FiLinkedin,
  FiSend,
  FiMessageCircle,
  FiAlertCircle,
  FiGithub
} from 'react-icons/fi';

import { submitToGoogleSheets } from '../context/AppContext';

// ── UPDATE THESE WITH YOUR REAL CONTACT DETAILS ───────────────────────────────
const MY_EMAIL = 'thesitepixel@gmail.com';
const MY_PHONE = '+91 8838027168';
const MY_PHONE_DIALABLE = '+918838027168';
const MY_WHATSAPP = '+918838027168';

const ContactPage = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    whatsappNumber: '',
    service: '',
    budget: '',
    message: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');

  const validate = () => {
    const e = {};

    if (!form.name.trim()) e.name = 'Name is required';

    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) {
      e.email = 'Valid email is required';
    }

    if (
      !form.whatsappNumber.trim() ||
      !/^[0-9]{10}$/.test(form.whatsappNumber)
    ) {
      e.whatsappNumber =
        'Valid 10 digit WhatsApp number is required';
    }

    if (!form.message.trim()) {
      e.message = 'Tell us about your project';
    }

    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errs = validate();

    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setErrors({});
    setSubmitting(true);
    setSubmitError('');

    try {
      await submitToGoogleSheets(form);
      setSubmitted(true);
    } catch (err) {
      setSubmitError(
        'Something went wrong. Please try again or email us directly.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const services = [
    'Web Development',
    'Web App Development',
    'UI/UX Design',
    'Branding',
    'Software Development',
    'Cyber Forensic Investigation',
    'Other'
  ];

  const budgets = [
    '₹5,000 – ₹15,000',
    '₹15,000 – ₹30,000',
    '₹50,000+',
    'Not sure yet'
  ];

  const contactItems = [
    {
      icon: <FiMail />,
      label: 'Email',
      value: MY_EMAIL,
      href: `mailto:${MY_EMAIL}`,
    },
    {
      icon: <FiPhone />,
      label: 'Phone',
      value: MY_PHONE,
      href: `tel:${MY_PHONE_DIALABLE}`,
    },
    {
      icon: <FiMapPin />,
      label: 'Location',
      value: 'Chennai, Tamil Nadu — Remote Worldwide',
      href: null,
    },
  ];

  return (
    <div className="min-h-screen pt-32 pb-24">

      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 mb-16">

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="font-mono text-xs text-purple-400 tracking-widest uppercase mb-4"
        >
          Get In Touch
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.55,
            ease: [0.25, 0.46, 0.45, 0.94]
          }}
          className="font-display text-6xl md:text-7xl font-normal text-white mb-4 leading-tight"
        >
          Let's Build
          <br />
          <em className="accent-text not-italic">Together</em>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.18 }}
          className="font-body text-white/50 text-lg max-w-xl leading-relaxed"
        >
          Tell us about your project and we'll respond within 24 hours
          with a tailored proposal.
        </motion.p>

      </div>

      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-3 gap-12">

        {/* Contact Info */}
        <div className="lg:col-span-1 space-y-5">

          {contactItems.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                delay: i * 0.1,
                ease: [0.25, 0.46, 0.45, 0.94]
              }}
              className="glass-card p-6 flex items-start gap-4"
            >

              <div
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-purple-400"
                style={{
                  background: 'rgba(127,4,155,0.2)'
                }}
              >
                {item.icon}
              </div>

              <div>
                <p className="font-body text-xs text-white/30 mb-1 uppercase tracking-wider">
                  {item.label}
                </p>

                {item.href ? (
                  <a
                    href={item.href}
                    className="font-body text-sm text-white hover:text-purple-400 transition-colors duration-200"
                  >
                    {item.value}
                  </a>
                ) : (
                  <p className="font-body text-sm text-white/70">
                    {item.value}
                  </p>
                )}
              </div>

            </motion.div>
          ))}

          {/* WhatsApp */}
          <motion.a
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.32 }}
            href={`https://wa.me/${MY_WHATSAPP.replace(/\D/g, '')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="glass-card p-6 flex items-center gap-4 hover:border-green-500/40 transition-all duration-300 cursor-pointer block group"
          >

            <div
              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-green-400 transition-transform duration-300 group-hover:scale-110"
              style={{
                background: 'rgba(34,197,94,0.15)'
              }}
            >
              <FiMessageCircle />
            </div>

            <div>
              <p className="font-body text-xs text-white/30 mb-1 uppercase tracking-wider">
                WhatsApp
              </p>

              <p className="font-body text-sm text-white">
                Chat with us directly
              </p>
            </div>

          </motion.a>

          {/* Socials */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.42 }}
            className="glass-card p-6"
          >

            <p className="font-body text-xs text-white/30 mb-4 uppercase tracking-wider">
              Follow Us
            </p>

            <div className="flex gap-3">

              {/* WhatsApp */}
              <a
                href="https://wa.me/918838027168"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full flex items-center justify-center text-white/50 hover:text-white border border-white/10 hover:border-green-500/50 hover:bg-green-500/10 transition-all duration-250"
              >
                <FiMessageCircle size={15} />
              </a>

              {/* Instagram */}
              <a
                href="https://www.instagram.com/sitepixel.in"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full flex items-center justify-center text-white/50 hover:text-white border border-white/10 hover:border-pink-500/50 hover:bg-pink-500/10 transition-all duration-250"
              >
                <FiInstagram size={15} />
              </a>

              {/* GitHub */}
              <a
                href="https://github.com/vimalpopzz"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full flex items-center justify-center text-white/50 hover:text-white border border-white/10 hover:border-white/50 hover:bg-white/10 transition-all duration-250"
              >
                <FiGithub size={15} />
              </a>

              {/* LinkedIn */}
              <a
                href="https://www.linkedin.com/in/vimal-popenzz-8aa808273/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full flex items-center justify-center text-white/50 hover:text-white border border-white/10 hover:border-blue-500/50 hover:bg-blue-500/10 transition-all duration-250"
              >
                <FiLinkedin size={15} />
              </a>

            </div>

          </motion.div>

        </div>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            delay: 0.18,
            duration: 0.55,
            ease: [0.25, 0.46, 0.45, 0.94]
          }}
          className="lg:col-span-2 glass-card p-10"
        >

          {submitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{
                duration: 0.4,
                ease: [0.25, 0.46, 0.45, 0.94]
              }}
              className="h-full flex flex-col items-center justify-center text-center py-20"
            >

              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{
                  delay: 0.15,
                  type: 'spring',
                  stiffness: 200,
                  damping: 15
                }}
                className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
                style={{
                  background: 'linear-gradient(135deg, #7f049b, #1c0c19)'
                }}
              >
                <FiSend size={28} className="text-white" />
              </motion.div>

              <motion.h2
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="font-display text-4xl font-normal text-white mb-3"
              >
                Message Sent!
              </motion.h2>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.42 }}
                className="font-body text-white/50 max-w-sm leading-relaxed"
              >
                Thank you for reaching out. We'll review your project details
                and get back to you within 24 hours.
              </motion.p>

            </motion.div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="space-y-6"
              noValidate
            >

              <h2 className="font-display text-3xl font-normal text-white mb-8">
                Tell Us About Your Project
              </h2>

              {submitError && (
                <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400">
                  <FiAlertCircle size={16} />
                  <p className="font-body text-sm">{submitError}</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                {/* Name */}
                <div>
                  <label
                    className="font-body text-xs text-white/40 tracking-widest uppercase mb-2 block"
                    htmlFor="name"
                  >
                    Your Name *
                  </label>

                  <input
                    id="name"
                    type="text"
                    value={form.name}
                    onChange={(e) =>
                      setForm({ ...form, name: e.target.value })
                    }
                    placeholder="Your Full Name"
                    autoComplete="name"
                    className={`input-field ${
                      errors.name ? 'error' : ''
                    }`}
                  />

                  {errors.name && (
                    <motion.p
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="font-body text-xs text-red-400 mt-1.5"
                    >
                      {errors.name}
                    </motion.p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label
                    className="font-body text-xs text-white/40 tracking-widest uppercase mb-2 block"
                    htmlFor="email"
                  >
                    Email Address *
                  </label>

                  <input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) =>
                      setForm({ ...form, email: e.target.value })
                    }
                    placeholder="hello@company.com"
                    autoComplete="email"
                    className={`input-field ${
                      errors.email ? 'error' : ''
                    }`}
                  />

                  {errors.email && (
                    <motion.p
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="font-body text-xs text-red-400 mt-1.5"
                    >
                      {errors.email}
                    </motion.p>
                  )}
                </div>

              </div>

              {/* WhatsApp Number */}
              <div>
                <label
                  className="font-body text-xs text-white/40 tracking-widest uppercase mb-2 block"
                  htmlFor="whatsappNumber"
                >
                  WhatsApp Number *
                </label>

                <input
                  id="whatsappNumber"
                  type="tel"
                  value={form.whatsappNumber}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      whatsappNumber: e.target.value.replace(/\D/g, '')
                    })
                  }
                  placeholder="Enter 10 digit WhatsApp number"
                  maxLength={10}
                  className={`input-field ${
                    errors.whatsappNumber ? 'error' : ''
                  }`}
                />

                {errors.whatsappNumber && (
                  <motion.p
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="font-body text-xs text-red-400 mt-1.5"
                  >
                    {errors.whatsappNumber}
                  </motion.p>
                )}
              </div>

              {/* Service */}
              <div>

                <label
                  className="font-body text-xs text-white/40 tracking-widest uppercase mb-2 block"
                  htmlFor="service"
                >
                  Service Needed
                </label>

                <select
                  id="service"
                  value={form.service}
                  onChange={(e) =>
                    setForm({ ...form, service: e.target.value })
                  }
                  className="input-field"
                  style={{
                    background: 'rgba(13,8,16,0.8)'
                  }}
                >

                  <option value="">
                    Select a service...
                  </option>

                  {services.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}

                </select>

              </div>

              {/* Budget */}
              <div>

                <label className="font-body text-xs text-white/40 tracking-widest uppercase mb-3 block">
                  Project Budget
                </label>

                <div className="flex flex-wrap gap-2">

                  {budgets.map((b) => (
                    <button
                      key={b}
                      type="button"
                      onClick={() =>
                        setForm({ ...form, budget: b })
                      }
                      className={`px-4 py-2 rounded-full font-body text-xs transition-all duration-250 ${
                        form.budget === b
                          ? 'text-white border border-purple-500 shadow-lg shadow-purple-500/20'
                          : 'text-white/50 border border-white/10 hover:border-white/25 hover:text-white/75'
                      }`}
                      style={
                        form.budget === b
                          ? {
                              background:
                                'rgba(127,4,155,0.28)'
                            }
                          : {}
                      }
                    >
                      {b}
                    </button>
                  ))}

                </div>

              </div>

              {/* Message */}
              <div>

                <label
                  className="font-body text-xs text-white/40 tracking-widest uppercase mb-2 block"
                  htmlFor="message"
                >
                  Project Details *
                </label>

                <textarea
                  id="message"
                  rows={5}
                  value={form.message}
                  onChange={(e) =>
                    setForm({ ...form, message: e.target.value })
                  }
                  placeholder="Tell us about your project, goals, and timeline..."
                  className={`input-field resize-none ${
                    errors.message ? 'error' : ''
                  }`}
                />

                {errors.message && (
                  <motion.p
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="font-body text-xs text-red-400 mt-1.5"
                  >
                    {errors.message}
                  </motion.p>
                )}

              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={submitting}
                className="btn-primary inline-flex items-center gap-2.5 px-10 py-3.5 disabled:opacity-60 disabled:cursor-not-allowed"
              >

                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    Send Message <FiSend size={15} />
                  </>
                )}

              </button>

            </form>
          )}

        </motion.div>

      </div>

    </div>
  );
};

export default ContactPage;